/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect } from 'react';
import { CardTextButtons } from '@gerdau/hefesto/CardTextButtons';
import { Title } from '@gerdau/hefesto/Title';
import { Container, EmitterReceiverContainer, CardsContainer } from './styles';
import {
  EOrderSettingsRoutes,
  longSteelRoutes
} from '../LongSteel/OrderSettingsMvp/orderSettings.routes';
import { ERoutes } from '../../../routes';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../store';
import { EPurchaseRoutes } from '../purchase.routes';
import {
  cleanSearchItemErrors,
  removeSearchedItem
} from '../../../store/ducks/long-steel-order-item-search/actions';
import { sendTagging } from '../../../store/ducks/tagging/actions';
import LoadingModal from '../../../components/LoadingModal';
import { CustomersState } from '../../../store/ducks/customers/types';
import { ECustomerTypeView } from '../../../store/ducks/user/types';
import { setShowCommerceTokenModal } from '../../../store/ducks/auth/actions';
import { GetFlag } from '../../../utils/featureFlag';
import OrderSettingsMvp from '../LongSteel/OrderSettingsMvp';
import useIsBusinessTypeCG from '../../../hooks/useIsBusinessTypeCG';

const PurchaseHome: React.FC = () => {
  const isBusinessTypeCG = useIsBusinessTypeCG();

  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const hasUserFabricationAccess = useSelector(
    (appState: ApplicationState) =>
      appState.user.userPermissions.hasFabricationAccess
  );

  const shoppingCartState = useSelector(
    (state: ApplicationState) => state.longSteelShoppingCart
  );

  const customersState = useSelector(
    (state: ApplicationState) => state.customers
  ) as CustomersState;

  const userState = useSelector((appState: ApplicationState) => appState.user);

  const orderItemSearch = useSelector(
    (state: ApplicationState) => state.longSteelOrderItemSearch
  );
  const cleanSearchedItems = () => {
    dispatch(removeSearchedItem([]));
  };

  const cleanErrors = () => {
    dispatch(cleanSearchItemErrors());
  };

  const commerceTokenModalFlag = GetFlag('commerce_token_modal');
  const newEmitterReceiverSelect = GetFlag('new_emitter_receiver_select');
  const migrationFlag = GetFlag('purchase-migration-brdi-brin');

  const commerceTokenAvailable = useSelector(
    (appState: ApplicationState) => appState.auth.commerceTokenAvailable
  );

  useEffect(() => {
    if (orderItemSearch?.searchedItems?.length > 0) {
      cleanSearchedItems();
    }
  });

  useEffect(() => {
    if (
      orderItemSearch?.searchingItemsError ||
      orderItemSearch?.searchingItemsSpreadsheetError ||
      orderItemSearch?.searchingItemsLastOrderError
    ) {
      cleanErrors();
    }
  });

  const redirectTo = (
    buttonLabel: string,
    midleOrderSettingsRoute: EOrderSettingsRoutes | EPurchaseRoutes,
    orderSettingsRoute?: EOrderSettingsRoutes | EPurchaseRoutes
  ) => {
    const isFabrication =
      midleOrderSettingsRoute === EPurchaseRoutes.FABRICATION ||
      midleOrderSettingsRoute === EPurchaseRoutes.FABRICATION_REVIEW_ORDER;
    let itemDescription = '';
    isFabrication
      ? (itemDescription = 'Corte_e_dobra')
      : (itemDescription = 'acos_longos');
    dispatch(
      sendTagging({
        screenName: location.pathname,
        event: 'click',
        itemName: `button:${buttonLabel.replaceAll(' ', '_')}`,
        itemDescription: itemDescription
      })
    );
    const route = isFabrication
      ? `${ERoutes.PURCHASE}/${EPurchaseRoutes.FABRICATION}/${
          midleOrderSettingsRoute === EPurchaseRoutes.FABRICATION
            ? ''
            : midleOrderSettingsRoute
        }`
      : `${ERoutes.PURCHASE}/${
          EOrderSettingsRoutes.LONG_STEEL
        }/${midleOrderSettingsRoute}/${orderSettingsRoute ?? ''}`;
    const showCommerceTokenModal =
      commerceTokenModalFlag &&
      !commerceTokenAvailable &&
      longSteelRoutes.some((longSteelRoute) => route.includes(longSteelRoute));
    if (showCommerceTokenModal) {
      dispatch(setShowCommerceTokenModal(showCommerceTokenModal, route));
    } else {
      dispatch(setShowCommerceTokenModal(showCommerceTokenModal));
      history.push(route, { cameFromPurchase: true });
    }
  };

  const customerViewType = useSelector(
    (appState: ApplicationState) => appState.user.searchCustomerType
  );
  const isEmitter = customerViewType === ECustomerTypeView.EMITTER;

  const isManagerContext = userState.isManager && migrationFlag;

  const isManagerBlockedByBP =
    isManagerContext &&
    (!shoppingCartState.emitterId ||
      !shoppingCartState.receiverId ||
      customersState.customers.find(
        (c) => c.customerId === shoppingCartState.emitterId
      )?.customerType === 'CG');

  const flowButtonsDisabled = isManagerContext
    ? isManagerBlockedByBP
    : newEmitterReceiverSelect &&
      (!shoppingCartState.emitterId || !shoppingCartState.receiverId);

  return (
    <Container>
      <header>
        <Title level={1}>Comprar aços</Title>
        <span>Selecione como você deseja comprar produtos ou serviços.</span>
      </header>

      {!customersState.loading && (
        <>
          <div className="cards">
            {isEmitter && !isBusinessTypeCG && (
              <>
                <div className="subtitle-cards">
                  <Title level={2}>Aços longos</Title>
                </div>
                {(newEmitterReceiverSelect || isManagerContext) && (
                  <EmitterReceiverContainer>
                    <span>
                      Selecione os dados abaixo para explorar o portfólio de
                      aços longos disponíveis para seu negócio
                    </span>
                    <OrderSettingsMvp />
                  </EmitterReceiverContainer>
                )}

                <CardsContainer disabledButtons={flowButtonsDisabled}>
                  <div className="card">
                    <CardTextButtons
                      title={'Comprar por vitrine'}
                      description={
                        'Explore o portifólio de produtos disponíveis para seu negócio através da vitrine.'
                      }
                      buttonProps={{
                        label: 'Comprar por vitrine',
                        onClick: flowButtonsDisabled
                          ? () => null
                          : () => {
                              redirectTo(
                                'Comprar por vitrine',
                                EPurchaseRoutes.COMMERCE,
                                EOrderSettingsRoutes.CATALOG
                              );
                            }
                      }}
                    />
                  </div>
                  <div className="card">
                    <CardTextButtons
                      title={'Comprar selecionando itens'}
                      description={
                        'Selecione e compre seus produtos pelos códigos de SKU ou descrição do material.'
                      }
                      buttonProps={{
                        label: 'Comprar selecionando itens',
                        onClick: flowButtonsDisabled
                          ? () => null
                          : () => {
                              redirectTo(
                                'Comprar selecionando itens',
                                EPurchaseRoutes.COMMERCE,
                                EOrderSettingsRoutes.SEARCH_ITEMS
                              );
                            }
                      }}
                    />
                  </div>
                  <div className="card">
                    <CardTextButtons
                      title={'Comprar por planilha'}
                      description={
                        'Preencha e envie uma planilha com os dados dos produtos desejados para montar o seu carrinho.'
                      }
                      buttonProps={{
                        label: 'Comprar por planilha',
                        onClick: flowButtonsDisabled
                          ? () => null
                          : () => {
                              redirectTo(
                                'Comprar por planilha',
                                EPurchaseRoutes.COMMERCE,
                                EOrderSettingsRoutes.SPREADSHEET_UPLOAD
                              );
                            }
                      }}
                    />
                  </div>
                  <div className="card">
                    <CardTextButtons
                      title={'Comprar por histórico'}
                      description={
                        'Abra seu histórico de pedidos para refazê-los.'
                      }
                      buttonProps={{
                        label: 'Comprar por histórico',
                        onClick: flowButtonsDisabled
                          ? () => null
                          : () => {
                              redirectTo(
                                'Comprar por histórico',
                                EPurchaseRoutes.COMMERCE,
                                EOrderSettingsRoutes.REPEAT_ORDER
                              );
                            }
                      }}
                    />
                  </div>
                </CardsContainer>
              </>
            )}
          </div>
          {hasUserFabricationAccess && (
            <div className="cards">
              <div className="subtitle-cards">
                <Title level={2}>Corte e dobra</Title>
              </div>
              <CardsContainer>
                <div className="card">
                  <CardTextButtons
                    title={'Solicitar novo pedido'}
                    description={
                      'Solicite aços cortados e dobrados de acordo com a necessidade dos seus projetos.'
                    }
                    buttonProps={{
                      label: 'Solicitar novo pedido',
                      onClick: () => {
                        redirectTo(
                          'Solicitar novo pedido',
                          EPurchaseRoutes.FABRICATION
                        );
                      }
                    }}
                  />
                </div>

                <div className="card">
                  <CardTextButtons
                    title={'Revisar pedido por histórico'}
                    description={
                      'Revise seus pedidos de aços cortados e dobrados a partir do seu histórico de compra.'
                    }
                    buttonProps={{
                      label: 'Revisar pedido por histórico',
                      onClick: () => {
                        redirectTo(
                          'Revisar pedido',
                          EPurchaseRoutes.FABRICATION_REVIEW_ORDER
                        );
                      }
                    }}
                  />
                </div>
              </CardsContainer>
            </div>
          )}
        </>
      )}
      <LoadingModal
        enabled={customersState.loading}
        title="Aguarde alguns instantes"
        message="Carregando os dados..."
      />
    </Container>
  );
};
export default PurchaseHome;
